import React, { useEffect, useState } from "react";
import { api, errMsg } from "../lib/api";
import AppShell from "../components/AppShell";
import { toast } from "sonner";
import {
  Warehouse,
  Plus,
  Trash2,
  Edit3,
  X,
  ShieldCheck,
  ShieldAlert,
  Users,
  Server,
  Globe,
  KeyRound,
  Link2,
  CheckCircle2,
  XCircle,
  Copy,
  Mail,
  Webhook,
} from "lucide-react";

/**
 * Admin Marketplace Management
 *
 * P1 of the Myntra production rollout:
 *  - View env-driven Myntra config (mode, base_url, credential presence)
 *  - CRUD Myntra warehouses (unique code per warehouse)
 *  - Assign / unassign warehouses to sellers (many-to-many)
 *  - Only admins land here (guarded server-side too)
 */
export default function AdminMarketplace() {
  const [config, setConfig] = useState(null);
  const [warehouses, setWarehouses] = useState([]);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // warehouse obj
  const [assignTarget, setAssignTarget] = useState(null); // warehouse obj
  const [assignSellerId, setAssignSellerId] = useState("");
  const [form, setForm] = useState(defaultForm());
  // Credentials management
  const [credentials, setCredentials] = useState([]);
  const [credTarget, setCredTarget] = useState(null);
  const [credForm, setCredForm] = useState({ secret_key: "", merchant_id: "", partner_email: "" });
  const [credBusy, setCredBusy] = useState(false);

  function defaultForm() {
    return {
      code: "",
      name: "",
      address: "",
      city: "",
      state: "",
      pincode: "",
      contact_name: "",
      contact_phone: "",
      gstin: "",
    };
  }

  async function loadAll() {
    setLoading(true);
    try {
      const [cfg, whs, sels, creds] = await Promise.allSettled([
        api.get("/admin/marketplace/myntra/config"),
        api.get("/admin/marketplace/myntra/warehouses"),
        api.get("/admin/sellers"),
        api.get("/admin/marketplace/myntra/credentials"),
      ]);
      if (cfg.status === "fulfilled") setConfig(cfg.value.data);
      if (whs.status === "fulfilled") setWarehouses(whs.value.data.warehouses || []);
      if (sels.status === "fulfilled") setSellers(sels.value.data || []);
      if (creds.status === "fulfilled") setCredentials(creds.value.data.sellers || []);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    loadAll();
  }, []);

  async function onConnectMyntra(e) {
    e.preventDefault();
    if (!credTarget) return;
    if (!credForm.secret_key.trim() || !credForm.merchant_id.trim() || !credForm.partner_email.trim()) {
      toast.error("All 3 fields are required");
      return;
    }
    setCredBusy(true);
    try {
      await api.post(
        `/admin/marketplace/myntra/credentials/${credTarget.seller_id}`,
        credForm
      );
      toast.success(`Myntra connected for ${credTarget.seller_name || credTarget.seller_email}`);
      setCredTarget(null);
      setCredForm({ secret_key: "", merchant_id: "", partner_email: "" });
      loadAll();
    } catch (err) {
      toast.error(errMsg(err, "Connect failed"));
    } finally {
      setCredBusy(false);
    }
  }

  async function onDisconnectMyntra(row) {
    if (!window.confirm(`Disconnect Myntra for ${row.seller_email}? Live sync will stop until re-connected.`)) return;
    try {
      await api.delete(`/admin/marketplace/myntra/credentials/${row.seller_id}`);
      toast.success("Disconnected");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err, "Disconnect failed"));
    }
  }

  async function onCreate(e) {
    e.preventDefault();
    if (!form.code.trim()) return toast.error("Warehouse code is required");
    try {
      await api.post("/admin/marketplace/myntra/warehouses", form);
      toast.success("Warehouse created");
      setShowCreate(false);
      setForm(defaultForm());
      loadAll();
    } catch (err) {
      toast.error(errMsg(err, "Failed to create warehouse"));
    }
  }

  async function onEdit(e) {
    e.preventDefault();
    if (!editTarget) return;
    try {
      const patch = { ...editTarget };
      delete patch.id;
      delete patch.code;
      delete patch.created_by;
      delete patch.created_at;
      delete patch.updated_at;
      delete patch.assigned_seller_ids;
      delete patch.assigned_seller_count;
      await api.patch(`/admin/marketplace/myntra/warehouses/${editTarget.id}`, patch);
      toast.success("Warehouse updated");
      setEditTarget(null);
      loadAll();
    } catch (err) {
      toast.error(errMsg(err, "Failed to update warehouse"));
    }
  }

  async function onDelete(w) {
    if (!window.confirm(`Delete warehouse ${w.code}? All seller assignments will be removed.`)) return;
    try {
      await api.delete(`/admin/marketplace/myntra/warehouses/${w.id}`);
      toast.success("Warehouse deleted");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err, "Delete failed"));
    }
  }

  async function onAssign(e) {
    e.preventDefault();
    if (!assignTarget || !assignSellerId) return toast.error("Pick a seller");
    try {
      await api.post(
        `/admin/marketplace/myntra/warehouses/${assignTarget.id}/assign`,
        { seller_id: assignSellerId }
      );
      toast.success("Seller assigned");
      setAssignSellerId("");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err, "Assign failed"));
    }
  }

  async function onUnassign(warehouseId, sellerId) {
    if (!window.confirm("Unassign this seller from the warehouse?")) return;
    try {
      await api.delete(`/admin/marketplace/myntra/warehouses/${warehouseId}/assign/${sellerId}`);
      toast.success("Seller unassigned");
      loadAll();
    } catch (err) {
      toast.error(errMsg(err, "Unassign failed"));
    }
  }

  const sellerById = React.useMemo(() => {
    const m = {};
    sellers.forEach((s) => (m[s.id] = s));
    return m;
  }, [sellers]);

  return (
    <AppShell role="admin">
      <div className="p-6 space-y-6" data-testid="admin-marketplace-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-[0.3em] text-indigo-400 font-bold">
              Marketplace management
            </div>
            <h1 className="font-display text-3xl font-black tracking-tighter mt-1 text-white">
              Myntra warehouses &amp; config
            </h1>
            <p className="text-sm text-slate-400 mt-1 max-w-2xl">
              Admin creates warehouse codes and assigns them to sellers. Sellers pick
              from their assigned warehouses when publishing listings or updating
              inventory. Environment-driven config toggles Mockify ↔ Production.
            </p>
          </div>
          <button
            onClick={() => setShowCreate(true)}
            className="btn-primary flex items-center gap-2"
            data-testid="create-warehouse-btn"
          >
            <Plus size={14} /> New warehouse
          </button>
        </div>

        {/* Config summary */}
        <ConfigPanel config={config} loading={loading} />

        {/* Webhook URLs to share with Myntra */}
        <WebhookUrlsPanel />

        {/* ── Myntra Credentials Manager ────────────────────────── */}
        <div
          className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-md overflow-hidden"
          data-testid="myntra-credentials-panel"
        >
          <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-2">
            <KeyRound size={16} className="text-indigo-300" />
            <div className="text-sm font-bold text-white">
              Myntra credentials ({credentials.filter((c) => c.connected).length}/{credentials.length} connected)
            </div>
            <span className="ml-auto text-[10px] text-white/40 font-mono">
              Admin-only · sellers never see these
            </span>
          </div>
          <div className="p-4 text-xs text-slate-400 border-b border-white/[0.05] leading-relaxed">
            Paste each seller&apos;s <strong className="text-white">secret_key</strong>,{" "}
            <strong className="text-white">merchant_id</strong> and{" "}
            <strong className="text-white">partner_email</strong> (the trio Myntra shares during
            OMS onboarding). We verify against Myntra live — invalid combos are rejected instantly.
          </div>
          {credentials.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">No sellers yet.</div>
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {credentials.map((c) => (
                <div key={c.seller_id} className="px-5 py-3 flex items-center gap-4" data-testid={`cred-row-${c.seller_email}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-sm font-bold text-white">{c.seller_name || "—"}</span>
                      <span className="text-xs text-slate-500 font-mono">· {c.seller_email}</span>
                      {c.connected ? (
                        <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold flex items-center gap-1">
                          <CheckCircle2 size={9} /> Connected
                        </span>
                      ) : (
                        <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-300 border border-slate-500/30 font-bold">
                          Not connected
                        </span>
                      )}
                    </div>
                    {c.connected && (
                      <div className="text-[11px] text-slate-500 mt-1 font-mono">
                        Merchant: {c.merchant_id} · Token expires {c.token_expires_at ? new Date(c.token_expires_at).toLocaleDateString() : "—"}
                      </div>
                    )}
                  </div>
                  {c.connected ? (
                    <>
                      <button
                        onClick={() => {
                          setCredTarget(c);
                          setCredForm({ secret_key: "", merchant_id: c.merchant_id || "", partner_email: c.partner_email || "" });
                        }}
                        className="text-[10px] uppercase tracking-widest font-bold text-indigo-300 hover:text-indigo-100 border border-indigo-400/30 px-2.5 py-1 rounded"
                        data-testid={`cred-reconnect-${c.seller_email}`}
                      >
                        Re-connect
                      </button>
                      <button
                        onClick={() => onDisconnectMyntra(c)}
                        className="text-[10px] uppercase tracking-widest font-bold text-rose-300 hover:text-rose-100 border border-rose-400/30 px-2.5 py-1 rounded"
                        data-testid={`cred-disconnect-${c.seller_email}`}
                      >
                        Disconnect
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => {
                        setCredTarget(c);
                        setCredForm({ secret_key: "", merchant_id: "", partner_email: "" });
                      }}
                      className="text-[10px] uppercase tracking-widest font-bold text-white bg-gradient-to-r from-indigo-500 to-fuchsia-500 px-3 py-1 rounded"
                      data-testid={`cred-connect-${c.seller_email}`}
                    >
                      Connect
                    </button>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Warehouses table */}
        <div
          className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-md overflow-hidden"
          data-testid="warehouses-table"
        >
          <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-2">
            <Warehouse size={16} className="text-indigo-300" />
            <div className="text-sm font-bold text-white">
              Warehouses ({warehouses.length})
            </div>
          </div>
          {loading ? (
            <div className="p-10 text-center text-sm text-slate-500">Loading…</div>
          ) : warehouses.length === 0 ? (
            <div className="p-10 text-center text-sm text-slate-500">
              No warehouses yet — click <span className="text-indigo-300 font-semibold">New warehouse</span> to add one.
            </div>
          ) : (
            <div className="divide-y divide-white/[0.05]">
              {warehouses.map((w) => (
                <div key={w.id} className="px-5 py-4" data-testid={`warehouse-row-${w.code}`}>
                  <div className="flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-bold text-white">{w.code}</span>
                        <span className="text-sm text-slate-300">· {w.name}</span>
                        {w.is_active ? (
                          <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30 font-bold">
                            Active
                          </span>
                        ) : (
                          <span className="text-[9px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-slate-500/15 text-slate-300 border border-slate-500/30 font-bold">
                            Inactive
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 mt-1">
                        {[w.city, w.state, w.pincode].filter(Boolean).join(" · ") || "—"}
                        {w.gstin && <> · GSTIN {w.gstin}</>}
                        {w.contact_phone && <> · {w.contact_phone}</>}
                      </div>

                      {/* Assigned sellers chips */}
                      <div className="mt-3 flex flex-wrap items-center gap-1.5">
                        <Users size={11} className="text-slate-500" />
                        <span className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mr-1">
                          {w.assigned_seller_count || 0} sellers
                        </span>
                        {(w.assigned_seller_ids || []).map((sid) => {
                          const s = sellerById[sid];
                          return (
                            <span
                              key={sid}
                              className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full text-[10px] font-semibold bg-indigo-500/15 text-indigo-200 border border-indigo-500/30"
                              data-testid={`assignment-${w.code}-${sid.slice(0, 8)}`}
                            >
                              {s ? s.name || s.email : sid.slice(0, 8)}
                              <button
                                onClick={() => onUnassign(w.id, sid)}
                                className="ml-0.5 rounded-full w-4 h-4 grid place-items-center hover:bg-indigo-500/30"
                                title="Unassign"
                                data-testid={`unassign-${w.code}-${sid.slice(0, 8)}`}
                              >
                                <X size={9} />
                              </button>
                            </span>
                          );
                        })}
                        <button
                          onClick={() => {
                            setAssignTarget(w);
                            setAssignSellerId("");
                          }}
                          className="text-[10px] uppercase tracking-widest text-indigo-300 hover:text-indigo-100 font-bold ml-1 border border-indigo-400/30 px-2 py-0.5 rounded-full"
                          data-testid={`assign-btn-${w.code}`}
                        >
                          + Assign
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0">
                      <button
                        onClick={() => setEditTarget({ ...w })}
                        className="p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/[0.06]"
                        title="Edit"
                        data-testid={`edit-warehouse-${w.code}`}
                      >
                        <Edit3 size={14} />
                      </button>
                      <button
                        onClick={() => onDelete(w)}
                        className="p-2 rounded-lg text-rose-400 hover:text-rose-200 hover:bg-rose-500/[0.1]"
                        title="Delete"
                        data-testid={`delete-warehouse-${w.code}`}
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create Modal */}
      <Modal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        title="New Myntra warehouse"
        testId="create-warehouse-modal"
      >
        <form onSubmit={onCreate} className="space-y-2.5">
          <Field label="Warehouse code (unique) *">
            <input
              required
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="WH-DEL-01"
              className={inputCls}
              data-testid="wh-form-code"
            />
          </Field>
          <Field label="Display name">
            <input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="Delhi Hub"
              className={inputCls}
              data-testid="wh-form-name"
            />
          </Field>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="City">
              <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} className={inputCls} data-testid="wh-form-city" />
            </Field>
            <Field label="State">
              <input value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} className={inputCls} data-testid="wh-form-state" />
            </Field>
          </div>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Pincode">
              <input value={form.pincode} onChange={(e) => setForm({ ...form, pincode: e.target.value })} className={inputCls} data-testid="wh-form-pincode" />
            </Field>
            <Field label="GSTIN">
              <input value={form.gstin} onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })} className={inputCls} data-testid="wh-form-gstin" />
            </Field>
          </div>
          <Field label="Address">
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className={inputCls} data-testid="wh-form-address" />
          </Field>
          <div className="grid grid-cols-2 gap-2.5">
            <Field label="Contact name">
              <input value={form.contact_name} onChange={(e) => setForm({ ...form, contact_name: e.target.value })} className={inputCls} data-testid="wh-form-contact" />
            </Field>
            <Field label="Contact phone">
              <input value={form.contact_phone} onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} className={inputCls} data-testid="wh-form-phone" />
            </Field>
          </div>
          <button type="submit" className="btn-primary w-full mt-2" data-testid="wh-form-submit">
            Create warehouse
          </button>
        </form>
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        title={`Edit ${editTarget?.code || ""}`}
        testId="edit-warehouse-modal"
      >
        {editTarget && (
          <form onSubmit={onEdit} className="space-y-2.5">
            <div className="text-xs text-slate-500">
              Code is immutable. Update address / contact / status below.
            </div>
            <Field label="Display name">
              <input value={editTarget.name || ""} onChange={(e) => setEditTarget({ ...editTarget, name: e.target.value })} className={inputCls} data-testid="wh-edit-name" />
            </Field>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="City">
                <input value={editTarget.city || ""} onChange={(e) => setEditTarget({ ...editTarget, city: e.target.value })} className={inputCls} />
              </Field>
              <Field label="State">
                <input value={editTarget.state || ""} onChange={(e) => setEditTarget({ ...editTarget, state: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <Field label="Pincode">
              <input value={editTarget.pincode || ""} onChange={(e) => setEditTarget({ ...editTarget, pincode: e.target.value })} className={inputCls} />
            </Field>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label="Contact name">
                <input value={editTarget.contact_name || ""} onChange={(e) => setEditTarget({ ...editTarget, contact_name: e.target.value })} className={inputCls} />
              </Field>
              <Field label="Contact phone">
                <input value={editTarget.contact_phone || ""} onChange={(e) => setEditTarget({ ...editTarget, contact_phone: e.target.value })} className={inputCls} />
              </Field>
            </div>
            <label className="inline-flex items-center gap-2 mt-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={!!editTarget.is_active}
                onChange={(e) => setEditTarget({ ...editTarget, is_active: e.target.checked })}
                data-testid="wh-edit-active"
              />
              Active (sellers can select this warehouse)
            </label>
            <button type="submit" className="btn-primary w-full mt-2" data-testid="wh-edit-submit">
              Save changes
            </button>
          </form>
        )}
      </Modal>

      {/* Assign Modal */}
      <Modal
        open={!!assignTarget}
        onClose={() => setAssignTarget(null)}
        title={`Assign ${assignTarget?.code || ""} to seller`}
        testId="assign-warehouse-modal"
      >
        {assignTarget && (
          <form onSubmit={onAssign} className="space-y-3">
            <Field label="Seller">
              <select
                value={assignSellerId}
                onChange={(e) => setAssignSellerId(e.target.value)}
                className={inputCls}
                data-testid="assign-seller-select"
                required
              >
                <option value="">— Pick a seller —</option>
                {sellers
                  .filter((s) => !(assignTarget.assigned_seller_ids || []).includes(s.id))
                  .map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name} · {s.email}
                    </option>
                  ))}
              </select>
            </Field>
            <button type="submit" className="btn-primary w-full" data-testid="assign-submit">
              Assign warehouse
            </button>
          </form>
        )}
      </Modal>

      {/* Myntra Connect Modal */}
      <Modal
        open={!!credTarget}
        onClose={() => setCredTarget(null)}
        title={`Connect Myntra for ${credTarget?.seller_name || credTarget?.seller_email || ""}`}
        testId="cred-connect-modal"
      >
        {credTarget && (
          <form onSubmit={onConnectMyntra} className="space-y-3">
            <div className="text-xs text-slate-400 leading-relaxed">
              Paste the credentials Myntra shared during OMS onboarding for{" "}
              <strong className="text-white">{credTarget.seller_email}</strong>. We verify them
              live — you&apos;ll get an instant error if any value is wrong.
            </div>
            <Field label="Secret key *">
              <input
                type="password"
                required
                value={credForm.secret_key}
                onChange={(e) => setCredForm({ ...credForm, secret_key: e.target.value })}
                placeholder="ex. sk_live_...
"
                className={inputCls}
                data-testid="cred-form-secret"
                autoComplete="off"
              />
            </Field>
            <Field label="Merchant ID *">
              <input
                required
                value={credForm.merchant_id}
                onChange={(e) => setCredForm({ ...credForm, merchant_id: e.target.value })}
                placeholder="ex. MID12345"
                className={inputCls}
                data-testid="cred-form-merchant"
              />
            </Field>
            <Field label="Partner email *">
              <input
                type="email"
                required
                value={credForm.partner_email}
                onChange={(e) => setCredForm({ ...credForm, partner_email: e.target.value })}
                placeholder="brand@myntra.com"
                className={inputCls}
                data-testid="cred-form-email"
              />
            </Field>
            <button
              type="submit"
              disabled={credBusy}
              className="btn-primary w-full disabled:opacity-50"
              data-testid="cred-form-submit"
            >
              {credBusy ? "Verifying with Myntra…" : "Connect & verify"}
            </button>
          </form>
        )}
      </Modal>
    </AppShell>
  );
}

const inputCls =
  "mt-1 w-full px-3 py-2 border border-white/[0.08] bg-white/[0.04] text-white placeholder:text-slate-500 rounded-md focus:outline-none focus:border-[#4F46E5]";

function Field({ label, children }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-widest font-bold text-slate-500">{label}</label>
      {children}
    </div>
  );
}

function Modal({ open, onClose, children, title, testId }) {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid={testId}>
      <div className="bg-slate-900 border border-white/[0.08] text-white rounded-xl w-full max-w-lg p-6 relative max-h-[90vh] overflow-y-auto">
        <button onClick={onClose} className="absolute top-3 right-3 text-slate-500 hover:text-white" data-testid="modal-close">
          <X size={18} />
        </button>
        <div className="font-display text-xl font-black tracking-tighter mb-4">{title}</div>
        {children}
      </div>
    </div>
  );
}

function ConfigPanel({ config, loading }) {
  const mode = config?.mode || "…";
  const isProd = mode === "production";
  return (
    <div
      className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-md p-5"
      data-testid="marketplace-config-panel"
    >
      <div className="flex items-center gap-2 mb-3">
        <Server size={15} className="text-indigo-300" />
        <div className="text-sm font-bold text-white">Runtime configuration</div>
        <span
          className={`ml-auto text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full font-bold border ${
            isProd
              ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30"
              : "bg-amber-500/15 text-amber-300 border-amber-500/30"
          }`}
          data-testid="config-mode-badge"
        >
          {loading ? "…" : isProd ? "Production" : "Mockify (sandbox)"}
        </span>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        <ConfigItem icon={Globe} label="Base URL" value={config?.base_url || "—"} mono />
        <ConfigItem
          icon={KeyRound}
          label="Client ID"
          value={config?.has_client_id ? "Set" : "Missing"}
          tone={config?.has_client_id ? "ok" : "warn"}
        />
        <ConfigItem
          icon={KeyRound}
          label="Client Secret"
          value={config?.has_client_secret ? "Set" : "Missing"}
          tone={config?.has_client_secret ? "ok" : "warn"}
        />
        <ConfigItem
          icon={Link2}
          label="Webhook Secret"
          value={config?.has_webhook_secret ? "Set" : "Missing"}
          tone={config?.has_webhook_secret ? "ok" : "warn"}
        />
      </div>
      {!isProd && (
        <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-amber-500/[0.06] border border-amber-500/25 text-xs text-amber-100">
          <ShieldAlert size={14} className="shrink-0 mt-0.5" />
          <div>
            <div className="font-bold">Sandbox mode active</div>
            <div className="mt-0.5 text-amber-200/80">
              To go live, set <code className="font-mono">MYNTRA_MODE=production</code> and the four
              production env vars (<code className="font-mono">MYNTRA_BASE_URL</code>,{" "}
              <code className="font-mono">MYNTRA_CLIENT_ID</code>,{" "}
              <code className="font-mono">MYNTRA_CLIENT_SECRET</code>,{" "}
              <code className="font-mono">MYNTRA_WEBHOOK_SECRET</code>) in <code>backend/.env</code>{" "}
              and restart. No code changes needed.
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ConfigItem({ icon: Icon, label, value, tone, mono }) {
  const iconCls =
    tone === "ok" ? "text-emerald-300" : tone === "warn" ? "text-amber-300" : "text-slate-400";
  const badgeIcon = tone === "ok" ? CheckCircle2 : tone === "warn" ? XCircle : null;
  return (
    <div className="p-3 rounded-lg bg-white/[0.02] border border-white/[0.05]">
      <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold flex items-center gap-1.5">
        <Icon size={11} className={iconCls} /> {label}
      </div>
      <div
        className={`mt-1 text-sm text-white flex items-center gap-1.5 ${mono ? "font-mono" : "font-semibold"}`}
      >
        {badgeIcon && React.createElement(badgeIcon, { size: 13, className: iconCls })}
        <span className="truncate">{value}</span>
      </div>
    </div>
  );
}

// ═══════════ Myntra Webhook URLs share panel ═════════════════════════
const MYNTRA_WEBHOOK_PATHS = [
  { path: "/api/myntra/webhooks/order/create",     label: "Order · Create",     desc: "New order placed" },
  { path: "/api/myntra/webhooks/order/update",     label: "Order · Update",     desc: "Order status transitions (shipped/delivered)" },
  { path: "/api/myntra/webhooks/order/cancel",     label: "Order · Cancel",     desc: "Order cancellation from customer or Myntra" },
  { path: "/api/myntra/webhooks/return/create",    label: "Return · Create",    desc: "Customer initiates a return" },
  { path: "/api/myntra/webhooks/return/update",    label: "Return · Update",    desc: "Return status transitions (picked/refunded)" },
  { path: "/api/myntra/webhooks/inventory/failed", label: "Inventory · Failed", desc: "Bulk inventory push failed on Myntra side" },
  { path: "/api/myntra/webhooks/token/refresh",    label: "Token · Refresh",    desc: "30-day token rotation callback" },
];

function WebhookUrlsPanel() {
  // Default to production so what admins copy is the URL Myntra should hit.
  // Preview env is auto-detected below so QA can also use this panel locally.
  const detectedBase =
    typeof window !== "undefined" && window.location.hostname.includes("preview.emergentagent.com")
      ? window.location.origin
      : "https://ecomcatalog.ai";
  const [base, setBase] = useState(detectedBase);

  function copy(text, label = "URL") {
    if (typeof navigator === "undefined" || !navigator.clipboard) {
      toast.error("Clipboard not available");
      return;
    }
    navigator.clipboard.writeText(text).then(
      () => toast.success(`${label} copied`),
      () => toast.error("Copy failed")
    );
  }

  const allUrls = MYNTRA_WEBHOOK_PATHS.map((w) => `${base}${w.path}`).join("\n");

  const emailBody = `Hi Myntra Partner Team,

We have successfully completed OMS integration on our platform (BrandBorn / ecomcatalog.ai). Please register the following 7 webhook URLs on our merchant account so live order, return and inventory events start flowing:

${MYNTRA_WEBHOOK_PATHS.map((w, i) => `${i + 1}. ${w.label}\n   ${base}${w.path}`).join("\n\n")}

Method: POST
Content-Type: application/json
Response: 2xx acknowledged within 5 seconds.

If HMAC signature verification is required, please share the signing secret so we can configure MYNTRA_WEBHOOK_SECRET on our side.

Thanks,
BrandBorn Team
support@brandborn.in`;

  return (
    <div
      className="rounded-2xl border border-white/[0.08] bg-white/[0.02] backdrop-blur-md overflow-hidden"
      data-testid="webhook-urls-panel"
    >
      <div className="px-5 py-4 border-b border-white/[0.06] flex items-center gap-2 flex-wrap">
        <Webhook size={16} className="text-indigo-300" />
        <div className="text-sm font-bold text-white">Myntra webhook URLs</div>
        <span className="text-[10px] text-white/40 font-mono ml-auto">
          Share these 7 URLs with Myntra to activate live events
        </span>
      </div>

      <div className="p-4 flex items-center gap-2 border-b border-white/[0.05]">
        <label className="text-[10px] uppercase tracking-widest text-white/45 font-bold whitespace-nowrap">
          Base URL
        </label>
        <input
          value={base}
          onChange={(e) => setBase(e.target.value)}
          className="flex-1 px-2.5 py-1.5 rounded-md bg-white/[0.04] border border-white/[0.08] text-xs font-mono text-white focus:outline-none focus:border-[#5B5CEB]"
          data-testid="webhook-base-input"
        />
        <button
          onClick={() => copy(allUrls, "All 7 URLs")}
          className="text-[10px] uppercase tracking-widest font-bold text-white bg-gradient-to-r from-[#5B5CEB] to-fuchsia-500 px-3 py-1.5 rounded-md flex items-center gap-1.5 shrink-0"
          data-testid="webhook-copy-all"
        >
          <Copy size={11} /> Copy all URLs
        </button>
        <button
          onClick={() => copy(emailBody, "Email draft")}
          className="text-[10px] uppercase tracking-widest font-bold text-indigo-300 border border-indigo-400/30 hover:text-white hover:border-indigo-400 px-3 py-1.5 rounded-md flex items-center gap-1.5 shrink-0"
          data-testid="webhook-copy-email"
        >
          <Mail size={11} /> Copy email draft
        </button>
      </div>

      <div className="divide-y divide-white/[0.05]">
        {MYNTRA_WEBHOOK_PATHS.map((w) => {
          const url = `${base}${w.path}`;
          return (
            <div
              key={w.path}
              className="px-5 py-2.5 flex items-center gap-3"
              data-testid={`webhook-row-${w.path.replace(/[^a-z]/gi, "-")}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-[12px] font-bold text-white">{w.label}</div>
                <div className="text-[10px] text-white/40 mt-0.5">{w.desc}</div>
                <div className="text-[11px] font-mono text-white/70 mt-1 truncate">{url}</div>
              </div>
              <button
                onClick={() => copy(url, w.label)}
                className="text-[10px] uppercase tracking-widest font-bold text-white/70 hover:text-white border border-white/[0.08] hover:border-white/[0.25] px-2.5 py-1 rounded flex items-center gap-1.5 shrink-0"
                data-testid={`webhook-copy-${w.path.replace(/[^a-z]/gi, "-")}`}
              >
                <Copy size={10} /> Copy
              </button>
            </div>
          );
        })}
      </div>

      <div className="p-4 text-[11px] text-slate-400 border-t border-white/[0.05] leading-relaxed">
        <strong className="text-white">How to activate:</strong> Send the email draft above (or share the URLs directly)
        to your Myntra account manager. Once they register these on the Myntra portal against your merchant ID,
        every order / return / inventory event will land in the seller&apos;s Command Center within seconds — no code changes needed on your end.
      </div>
    </div>
  );
}

